import React, { useState, useRef, useEffect } from 'react';
import { Element, ElementType } from '../types';
import { 
  Play, Pause, Trash2, Save, FolderOpen, 
  Menu, Settings, Image,
  Volume2, VolumeX
} from 'lucide-react';
import { audioManager } from '../audio/AudioManager';

interface ToolbarProps {
  elements: Element[];
  selectedElementId: string;
  setSelectedElementId: (id: string) => void;
  brushSize: number;
  setBrushSize: (size: number) => void;
  isPlaying: boolean;
  onPlayPause: () => void;
  onClear: () => void;
  onOpenRoadmap: () => void;
  onOpenSettings: () => void;
  onOpenSceneSelector: () => void;
  onSave: () => void;
  onLoad: (file: File) => void;
  usedElements?: Set<string>;
  navCollapsed?: boolean;
  onToggleNav?: () => void;
}

const Toolbar: React.FC<ToolbarProps> = ({
  elements,
  selectedElementId,
  setSelectedElementId,
  brushSize,
  setBrushSize,
  isPlaying,
  onPlayPause,
  onClear,
  onOpenRoadmap,
  onOpenSettings,
  onOpenSceneSelector,
  onSave,
  onLoad,
  usedElements = new Set(),
  navCollapsed = false,
  onToggleNav,
}) => {
  const [selectedTab, setSelectedTab] = useState<'elements' | 'spells'>('elements');
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize audio state
  useEffect(() => {
    const settings = audioManager.getSettings();
    setAudioEnabled(settings.enabled);
  }, []);

  const toggleAudio = () => {
    const newEnabled = audioManager.toggleMute();
    setAudioEnabled(newEnabled);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onLoad(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const elementItems = elements.filter(el => 
    !['TORNADO', 'LIGHTNING', 'METEOR', 'BLACKHOLE', 'RAINCLOUD', 'BOMB', 'FREEZE_RAY', 'EARTHQUAKE', 'TSUNAMI'].includes(el.id)
  );
  
  const spellItems = elements.filter(el => 
    ['TORNADO', 'LIGHTNING', 'METEOR', 'BLACKHOLE', 'RAINCLOUD', 'BOMB', 'FREEZE_RAY', 'EARTHQUAKE', 'TSUNAMI'].includes(el.id)
  );

  const isSpell = (id: string) => ['TORNADO', 'LIGHTNING', 'METEOR', 'BLACKHOLE', 'RAINCLOUD', 'BOMB', 'FREEZE_RAY', 'EARTHQUAKE', 'TSUNAMI'].includes(id);

  return (
    <div className="absolute top-4 left-4 z-10 flex flex-col gap-3">
      {/* When collapsed: only hamburger for full-screen */}
      {navCollapsed ? (
        <button
          onClick={onToggleNav}
          className="p-2.5 bg-gray-800/90 backdrop-blur-md rounded-xl shadow-xl border border-gray-700/50 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
          title="Show menu"
        >
          <Menu className="w-6 h-6" />
        </button>
      ) : (
      <>
      {/* Main Toolbar */}
      <div className="bg-gray-800/90 backdrop-blur-md rounded-xl p-3 shadow-xl border border-gray-700/50">
        {/* Top Row - Controls: Play, Clear, Save, Load, Scenes, Audio, Hamburger, Settings */}
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-700/50">
          <button
            onClick={onPlayPause}
            className={`p-2 rounded-lg transition-colors ${
              isPlaying 
                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
            }`}
            title={isPlaying ? 'Pause Simulation' : 'Resume Simulation'}
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>

          <button
            onClick={onClear}
            className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors"
            title="Clear Canvas"
          >
            <Trash2 className="w-5 h-5" />
          </button>

          <div className="w-px h-6 bg-gray-700/50 mx-1" />

          <button
            onClick={onSave}
            className="p-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-lg transition-colors"
            title="Save Scene"
          >
            <Save className="w-5 h-5" />
          </button>

          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-lg transition-colors"
            title="Load Scene"
          >
            <FolderOpen className="w-5 h-5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileChange}
            className="hidden"
          />

          <div className="w-px h-6 bg-gray-700/50 mx-1" />

          <button
            onClick={onOpenSceneSelector}
            className="p-2 bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 rounded-lg transition-colors"
            title="Pre-built Scenes"
          >
            <Image className="w-5 h-5" />
          </button>

          <button
            onClick={toggleAudio}
            className={`p-2 rounded-lg transition-colors ${
              audioEnabled 
                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
            }`}
            title={audioEnabled ? 'Mute Audio' : 'Enable Audio'}
          >
            {audioEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>

          <button
            onClick={onToggleNav}
            className="p-2 bg-gray-700 text-gray-300 hover:bg-gray-600 rounded-lg transition-colors"
            title="Collapse menu (full screen)"
          >
            <Menu className="w-5 h-5" />
          </button>

          <button
            onClick={onOpenSettings}
            className="p-2 bg-gray-700 text-gray-300 hover:bg-gray-600 rounded-lg transition-colors"
            title="Settings"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-3">
          <button
            onClick={() => setSelectedTab('elements')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              selectedTab === 'elements'
                ? 'bg-blue-500/20 text-blue-400'
                : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Elements
          </button>
          <button
            onClick={() => setSelectedTab('spells')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              selectedTab === 'spells'
                ? 'bg-purple-500/20 text-purple-400'
                : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Spells
          </button>
        </div>

        {/* Brush Size */}
        <div className="mb-3">
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span>Brush Size</span>
            <span>{brushSize}px</span>
          </div>
          <input
            type="range"
            min="1"
            max="20"
            value={brushSize}
            onChange={(e) => setBrushSize(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
        </div>

        {/* Element Grid */}
        <div className="grid grid-cols-6 gap-1.5 max-h-[50vh] overflow-y-auto pr-1 scrollbar-thin">
          {(selectedTab === 'elements' ? elementItems : spellItems).map((el) => {
            const isSelected = selectedElementId === el.id;
            const hasBeenUsed = usedElements.has(el.id);
            
            return (
              <button
                key={el.id}
                onClick={() => setSelectedElementId(el.id)}
                onMouseEnter={() => setShowTooltip(el.id)}
                onMouseLeave={() => setShowTooltip(null)}
                className={`
                  relative aspect-square rounded-lg transition-all duration-150
                  flex items-center justify-center
                  ${isSelected 
                    ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800 scale-105' 
                    : 'hover:scale-105'
                  }
                  ${hasBeenUsed && !isSelected ? 'opacity-90' : ''}
                  ${!hasBeenUsed && !isSelected && selectedTab === 'elements' ? 'opacity-70' : ''}
                `}
                style={{ backgroundColor: el.color }}
                title={el.name}
              >
                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute inset-0 rounded-lg bg-white/20" />
                )}

                {/* New element indicator */}
                {!hasBeenUsed && selectedTab === 'elements' && !isSelected && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-400 rounded-full" />
                )}

                {/* Spell indicator */}
                {isSpell(el.id) && (
                  <div className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 bg-yellow-400 rounded-full" />
                )}

                {/* Tooltip */}
                {showTooltip === el.id && (
                  <div className="absolute left-full ml-2 z-50 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-xl border border-gray-700">
                    <div className="font-semibold">{el.name}</div>
                    <div className="text-gray-400 max-w-[150px] whitespace-normal">{el.description}</div>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Selected Element Info */}
        <div className="mt-3 pt-3 border-t border-gray-700/50">
          {(() => {
            const selectedEl = elements.find(el => el.id === selectedElementId);
            if (!selectedEl) return null;
            return (
              <div className="flex items-center gap-3">
                <div 
                  className="w-8 h-8 rounded-lg flex-shrink-0"
                  style={{ backgroundColor: selectedEl.color }}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white text-sm truncate">{selectedEl.name}</div>
                  <div className="text-xs text-gray-400 truncate">{selectedEl.description}</div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default Toolbar;
